import React from 'react';
import { IconButton, Stack, TextField, Box } from '@mui/material';
import { Button, MiniLoading, Text, rem } from '@innovyze/stylovyze';
import { DeleteForever } from '@mui/icons-material';
import { useGlobalization } from '@Translations';
import { CurvePoint } from './utils';

interface CustomCurveEditorProps {
	customCurvePoints: CurvePoint[];
	setCustomCurvePoints: React.Dispatch<React.SetStateAction<CurvePoint[]>>;
	isLoading: boolean;
}

export const CustomCurveEditor = ({ customCurvePoints, setCustomCurvePoints, isLoading }: CustomCurveEditorProps) => {
	const { t } = useGlobalization();

	const handleAgeChange = (index: number, value: number | null) => {
		setCustomCurvePoints(prevState => prevState.map((p, i) => (i === index ? { ...p, age: value } : p)));
	};

	const handleSurvivalProbabilityChange = (index: number, value: number | null) => {
		setCustomCurvePoints(prevState =>
			prevState.map((p, i) => (i === index ? { ...p, survivalProbability: value } : p)),
		);
	};

	const handleRemovePoint = (index: number) => {
		setCustomCurvePoints(prevState => prevState.filter((_, i) => i !== index));
	};

	const handleAddPoint = () => {
		setCustomCurvePoints(prevState => [...prevState, { age: null, survivalProbability: null }]);
	};

	if (isLoading) {
		return <MiniLoading loading={isLoading} />;
	}

	return (
		<Stack spacing={2}>
			<Box
				sx={{
					maxHeight: rem(300),
					overflow: 'auto',
					paddingRight: rem(8),
					paddingTop: rem(8),
				}}>
				{customCurvePoints.map((point, index) => (
					<Stack
						key={index}
						direction="row"
						justifyContent="space-around"
						spacing={rem(8)}
						alignItems="center"
						sx={{ mb: rem(16) }}>
						<Text>{`Point ${index}`}</Text>
						<TextField
							label={<Text>{t('Age (Years)')}</Text>}
							variant="outlined"
							size="small"
							value={point.age ?? ''}
							onChange={e => handleAgeChange(index, e.target.value === '' ? null : Number(e.target.value))}
						/>
						<TextField
							label={<Text>{t('Survival Probability (%)')}</Text>}
							variant="outlined"
							size="small"
							value={point.survivalProbability ?? ''}
							onChange={e =>
								handleSurvivalProbabilityChange(index, e.target.value === '' ? null : Number(e.target.value))
							}
						/>
						<IconButton onClick={() => handleRemovePoint(index)} size="small">
							<DeleteForever />
						</IconButton>
					</Stack>
				))}
			</Box>
			<Button color="primary" onClick={handleAddPoint}>
				{t('Add another point')}
			</Button>
		</Stack>
	);
};
